import { isAxiosError } from "axios";

import { NotFoundError } from "../errors";

import { QueryStatusAssertionError } from "./query-status-assertion-error";

// Statuses whose failure is about timing rather than about the request itself.
const RETRIABLE_STATUSES = new Set([408, 425, 429]);

const TRANSIENT_MESSAGE_PATTERN =
  /network error|failed to fetch|load failed|networkerror/i;

const MAX_CAUSE_DEPTH = 5;

// Query errors reach error boundaries wrapped: an assertQuery failure carries
// the Axios error as its cause, itself sometimes re-wrapped by the boundary.
export function getHttpErrorStatus(
  error: unknown,
  depth = 0,
): number | undefined {
  if (depth > MAX_CAUSE_DEPTH) {
    return undefined;
  }
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  if (error instanceof NotFoundError) {
    return 404;
  }
  if (error instanceof QueryStatusAssertionError) {
    return (
      error.errorPayload?.status ?? getHttpErrorStatus(error.cause, depth + 1)
    );
  }
  if (error instanceof Error && error.cause !== undefined) {
    return getHttpErrorStatus(error.cause, depth + 1);
  }
  return undefined;
}

// An entity that is gone is a state to render, not a failure to report: call
// sites check this before asserting so the 404 never reaches an error boundary.
export function isQueryEntityGone(query: {
  status: string;
  error: unknown;
}): boolean {
  return query.status === "error" && getHttpErrorStatus(query.error) === 404;
}

// Allowlist, not denylist: anything we cannot positively identify as transient
// (zod parse failures, business-rule throws inside a queryFn) must fail fast
// instead of paying three round-trips before the error boundary shows.
//
// No idempotent-method restriction on purpose: this predicate only feeds
// query retries (defaultOptions.queries / retryAsync), never mutations, and
// TanStack already re-runs queryFns freely (focus, reconnect, invalidation) —
// a queryFn that is not safe to re-execute is broken regardless of retries.
// Some read endpoints are POSTs (search-style, e.g. lcm deals), and they need
// this protection as much as the GETs do.
export function isRetriableQueryError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined) {
      // No response at all: DNS, offline, aborted connection, timeout.
      return true;
    }
    return status >= 500 || RETRIABLE_STATUSES.has(status);
  }
  // Amplify/AppSync surfaces connectivity failures as a plain Error
  // ("A network error has occurred."), with no status to inspect.
  return (
    error instanceof Error && TRANSIENT_MESSAGE_PATTERN.test(error.message)
  );
}
